import { InjectionToken } from '@jack-henry/frontend-utils/di';
import {
    DynamicComponentExitReason,
    DynamicComponentOptions,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { Observable } from '@jack-henry/frontend-utils/observable';
import { querySlot } from '@treasury/utils/functions';
import type { HTMLTemplateResult, PropertyValues } from 'lit';

export const DRAWER_CONTENT_CONTAINER_ID = 'tm-side-drawer-content';

/**
 * Get drawer content from the slot containing it in `<tm-side-drawer>`.
 */
export function getContentFromSlot(slot: HTMLSlotElement) {
    const container = querySlot(slot, `#${DRAWER_CONTENT_CONTAINER_ID}`);
    return container?.firstElementChild;
}

export enum SideDrawerButtonPosture {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
    Constructive = 'approve',
    Destructive = 'reject',
}

export interface SideDrawerButton<T = unknown> {
    label: string | (() => string);
    reason: DynamicComponentExitReason;
    posture?: SideDrawerButtonPosture;
    /**
     * Provide a custom predicate to determine if the button is enabled.
     *
     * @param data Any data that has been set from within the drawer using `setData()`
     * @returns `true`, if the button is enabled.
     */
    checkEnabled?: (data?: T) => boolean;
    onClick?: (close: (data?: T) => void, setData: (data: T) => void) => void | Promise<void>;
}

export interface SideDrawerOptions extends DynamicComponentOptions {
    align: 'left' | 'right';
    footerAlignment: 'left' | 'right';
    buttons: SideDrawerButton[];
    promptOnClose: boolean;
    /**
     * If true, clicking outside the drawer will close it.
     */
    closeOnBlur: boolean;
}

const DRAWER_RESULT_EVENT = `tm-drawer-result`;

export function listenForDrawerResult<T>(element: Element, callback: (data: T) => void) {
    const listener = (e: Event) => {
        if (!(e instanceof CustomEvent)) {
            throw new Error(`Invalid event instance for "${DRAWER_RESULT_EVENT}"`);
        }

        callback((e as CustomEvent<T>).detail);
    };

    element.addEventListener(DRAWER_RESULT_EVENT, listener);

    return {
        unsubscribe: () => element.removeEventListener(DRAWER_RESULT_EVENT, listener),
    };
}

/**
 * Dispatch side drawer data on an element in a strongly-typed way.
 * Intended to be used from a component rendered within `TmSideDrawer`.
 */
export function dispatchDrawerResult<T>(element: Element, data: T) {
    const event = new CustomEvent(DRAWER_RESULT_EVENT, {
        detail: data,
        bubbles: true,
        composed: true,
    });

    element.dispatchEvent(event);
}

/**
 * Public surface area of an element that receives
 * additional capabilities when hosted as side drawer content.
 *
 * This primarily exists because the main class that implements
 * this interface extends `TmBaseComponent` which itself references Tailwind.
 * Since the side drawer is used in channel, a direct reference to `TmBaseComponent`
 * would cause the build to fail so this functionality must be referenced indirectly
 * via the interface and accompanying type guard.
 */
export interface ISideDrawerContent<T = unknown> {
    drawerButtons?: SideDrawerButton<T>[];
    updated$: Observable<PropertyValues>;
}

export function isSideDrawerContent<T>(element: object): element is ISideDrawerContent<T> {
    return (
        'updated$' in element &&
        element.updated$ instanceof Observable &&
        'drawerButtons' in element
    );
}

export type SideDrawerPromptFn = (
    content: string | HTMLTemplateResult,
    title: string
) => Promise<{ reason: DynamicComponentExitReason }>;
export const DrawerConfirmationPromptToken = new InjectionToken<SideDrawerPromptFn>(
    'DrawerConfirmationPrompt'
);
